/**
 * PeruAI Page - Election Information Chat Interface
 * Guest-first design: users can chat without login
 */
import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faPaperPlane, faPlus } from '@fortawesome/free-solid-svg-icons';

// Components
import { Button, ThemeToggle } from '@/components/ui';
import { Input } from '@/components/forms';
import { ChatInterface } from '@/components/features/ChatInterface';
import { ConversationList } from '@/components/features/ConversationList';
import { CandidateCarousel } from '@/components/features/CandidateCarousel';
import { SEO } from '@/components/features/SEO';

// Hooks and stores
import { useChatStore, useAuthStore, useModalStore } from '@/stores';
import { useConversation, useConversations, useSendMessage } from '@/api';
import { ROUTES } from '@/constants/routes';
import { ConfirmModal } from '@/components/modals/ConfirmModal/ConfirmModal';

// Types
import type { CandidateListItem } from '@/types/elections';

import styles from './PeruAI.module.css';

export function PeruAI() {
    const navigate = useNavigate();

    // Auth state
    const { isLogged } = useAuthStore();
    const { openModal } = useModalStore();

    // Chat state
    const {
        sidebarOpen,
        toggleSidebar,
        setSidebarOpen,
        currentConversationId,
        setCurrentConversation,
        messageInputValue,
        setMessageInput,
        clearMessageInput,
        isAiTyping,
        selectedCandidateId,
        setSelectedCandidate,
        initGuestSession,
    } = useChatStore();

    // Data fetching
    const { data: conversations } = useConversations();
    const { data: currentConversation, isLoading: isConversationLoading } = useConversation(currentConversationId);
    const sendMessageMutation = useSendMessage();

    // Initialize guest session on mount
    useEffect(() => {
        if (!isLogged) {
            initGuestSession();
        }
    }, [isLogged, initGuestSession]);

    // Handle candidate selection - prefill question
    const handleCandidateSelect = useCallback((candidate: CandidateListItem) => {
        setSelectedCandidate(candidate.id);
        setMessageInput(`¿Cuáles son las propuestas de ${candidate.full_name}?`);
    }, [setSelectedCandidate, setMessageInput]);

    // Start a fresh conversation
    const startFreshChat = useCallback(() => {
        setCurrentConversation(null);
        clearMessageInput();
        setSelectedCandidate(null);
        setSidebarOpen(false);
    }, [setCurrentConversation, clearMessageInput, setSelectedCandidate, setSidebarOpen]);

    // Handle new chat - show signup prompt for guests if they have existing conversation
    const handleNewChat = useCallback(() => {
        if (!isLogged && currentConversationId) {
            openModal(
                <ConfirmModal
                    title="¿Crear una cuenta?"
                    message="Crea una cuenta para guardar tus conversaciones, o continúa sin cuenta para iniciar una nueva."
                    confirmLabel="Registrarme"
                    cancelLabel="Continuar sin cuenta"
                    onConfirm={() => navigate(ROUTES.register)}
                    onCancel={startFreshChat}
                />
            );
            return;
        }

        startFreshChat();
    }, [isLogged, currentConversationId, navigate, openModal, startFreshChat]);

    // Handle conversation selection
    const handleSelectConversation = useCallback((id: number) => {
        setCurrentConversation(id);
        // Close sidebar on mobile after selecting a conversation
        if (window.innerWidth < 768) {
            setSidebarOpen(false);
        }
    }, [setCurrentConversation, setSidebarOpen]);

    // Pending message for optimistic updates
    const [pendingMessage, setPendingMessage] = useState<string | null>(null);

    // Handle message send
    const handleSendMessage = useCallback((contentOrEvent?: string | React.MouseEvent) => {
        // Allow passing content directly (for suggestions) or use input value
        const content = typeof contentOrEvent === 'string' ? contentOrEvent : messageInputValue.trim();

        if (!content || sendMessageMutation.isPending) return;

        // Guest limit check: if guest with no current conversation but has existing ones
        const guestHasExistingChats = !isLogged && !currentConversationId && conversations && conversations.length > 0;

        if (guestHasExistingChats) {
            openModal(
                <ConfirmModal
                    title="Conversación activa"
                    message="Los invitados solo pueden tener 1 conversación. Regístrate para tener chats ilimitados, o continúa con tu conversación existente."
                    confirmLabel="Registrarme"
                    cancelLabel="Continuar conversación"
                    onConfirm={() => navigate(ROUTES.register)}
                    onCancel={() => {
                        const existingChat = conversations![0];
                        setCurrentConversation(existingChat.id);
                    }}
                />
            );
            return;
        }

        // Optimistic update - show message immediately
        setPendingMessage(content);
        clearMessageInput();

        // Collapse sidebar when chatting
        setSidebarOpen(false);

        sendMessageMutation.mutate({
            conversationId: currentConversationId ?? 'new',
            content,
            candidateId: selectedCandidateId ?? undefined,
        }, { onSuccess: () => setPendingMessage(null) });

        // Clear candidate selection after sending
        setSelectedCandidate(null);
    }, [
        messageInputValue,
        sendMessageMutation,
        currentConversationId,
        selectedCandidateId,
        setSelectedCandidate,
        clearMessageInput,
        setSidebarOpen,
        isLogged,
        conversations,
        navigate,
        setCurrentConversation
    ]);

    // Handle Enter key in input
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    }, [handleSendMessage]);

    // Handle suggestion chip click
    const handleSuggestionClick = useCallback((suggestion: string) => {
        handleSendMessage(suggestion);
    }, [handleSendMessage]);

    // Get messages from current conversation
    const messages = currentConversation?.messages ?? [];

    return (
        <div className={styles.layout}>
            <SEO
                title="Peru IA - Chat de Elecciones 2026"
                description="Haz preguntas y conoce las propuestas de los candidatos a la presidencia de Peru 2026 usando Inteligencia Artificial. Respuestas instantaneas y sin sesgos."
                url="https://peruhonesto.com/peruai"
                keywords={[
                    "chat elecciones peru 2026",
                    "preguntas candidatos peru",
                    "ia elecciones peru",
                    "propuestas candidatos 2026",
                    "asistente electoral peru"
                ]}
            />
            {/* Sidebar */}
            <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
                <ConversationList
                    onNewChat={handleNewChat}
                    onSelectConversation={handleSelectConversation}
                    currentConversationId={currentConversationId}
                />
            </aside>

            {/* Sidebar overlay for mobile */}
            {sidebarOpen && (
                <div
                    className={styles.sidebarOverlay}
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main content area */}
            <main className={styles.main}>
                {/* Header */}
                <header className={styles.header}>
                    <div className={styles.headerLeft}>
                        <button
                            className={styles.iconButton}
                            onClick={toggleSidebar}
                            aria-label="Abrir menú"
                        >
                            <FontAwesomeIcon icon={faBars} />
                        </button>
                        <button
                            className={styles.iconButton}
                            onClick={handleNewChat}
                            aria-label="Nueva conversación"
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </button>
                    </div>

                    <div
                        className={styles.headerCenter}
                        onClick={() => navigate(ROUTES.home)}
                    >
                        <img src="/logo.png" alt="" className={styles.headerLogo} />
                        <h1 className={styles.title}>Perú IA</h1>
                    </div>

                    <div className={styles.headerRight}>
                        <ThemeToggle size="md" />
                        {!isLogged && (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(ROUTES.login)}
                                className={styles.headerActionsButton}
                            >
                                Iniciar sesión
                            </Button>
                        )}
                    </div>
                </header>

                {/* Chat area */}
                <section className={styles.chatSection}>
                    <ChatInterface
                        messages={messages}
                        isAiTyping={isAiTyping}
                        pendingMessage={pendingMessage}
                        isLoading={isConversationLoading}
                        emptyStateContent={
                            <div className={styles.carouselWrapper}>
                                <p className={styles.carouselHint}>
                                    Haz clic en un candidato para generar una pregunta
                                </p>
                                <CandidateCarousel
                                    onCandidateSelect={handleCandidateSelect}
                                    selectedCandidateId={selectedCandidateId}
                                />
                            </div>
                        }
                        onSuggestionClick={handleSuggestionClick}
                    />
                </section>

                {/* Message input */}
                <section className={styles.inputSection}>
                    <div className={styles.inputWrapper} onKeyDown={handleKeyDown}>
                        <Input
                            name="message-input"
                            value={messageInputValue}
                            setValue={setMessageInput}
                            placeholder="Escribe tu pregunta sobre los candidatos..."
                            variant="flat"
                            radius="lg"
                            multiline
                            rows={1}
                        />
                        <Button
                            variant="primary"
                            onClick={handleSendMessage}
                            disabled={!messageInputValue.trim() || sendMessageMutation.isPending}
                            className={styles.sendButton}
                        >
                            <FontAwesomeIcon icon={faPaperPlane} />
                        </Button>
                    </div>
                    <p className={styles.disclaimer}>
                        Esta IA podría cometer errores. No hace recomendaciones de voto.
                    </p>
                </section>
            </main>
        </div>
    );
}
